import { appColors } from '@base/useTheme';
import { CustomColumnRepresentation, CustomColumnType, SheetSortDirection } from '@common/api';
import { Confirmation } from '@components/confirmation/Confirmation';
import { ContextMenu, ContextMenuRenderProps } from '@components/context-menu/ContextMenu';
import { ArrowUp, ArrowDown, Checked, Copy, Delete, Edit, Sort, UnChecked } from '@components/icons/FarseerIcons';
import { useModal } from '@components/modal/ModalProvider';
import { useI18nContext } from '@i18n/i18n-react';
import { useLogEvent } from '@providers/TrackingProvider';
import { generateUniqueId } from '@util/helpers';
import { $splice, $toggle, $updateItem } from '@util/patch';
import { useSheetEditorContext } from '../editor/SheetEditorProvider';
import { CustomColumnModal } from './CustomColumnModal';
import { useSheetCustomColumns } from './useSheetCustomColumns';
import { TextInput } from '@components/form';
import { FlexRow } from '@components/layout/Flex';
import { useDerivedState } from '@hooks/useDerivedState';
import { useSheetEditorSort } from '../editor/useSheetEditorSort';
import { SheetFreezeMenu } from '../context-menu/SheetFreezeMenu';

export const SheetCustomColumnsEditMenu = ({
    columnKey,
    columnIndex,
    ...menuProps
}: {
    columnKey: string;
    columnIndex: number;
} & ContextMenuRenderProps) => {
    const { LL } = useI18nContext();
    const { showModal, hideModal } = useModal();
    const logEvent = useLogEvent();
    const {
        state: { canEditSheet: canEdit, referencing },
    } = useSheetEditorContext();

    const { customColumns, customColumnLabels, customColumnIndices, updateCustomColumns } = useSheetCustomColumns();

    const { addRule } = useSheetEditorSort();

    const customColumnIdx = customColumnIndices[columnKey];
    const currColumn = customColumns[customColumnIdx];

    const [editableName, setEditableName] = useDerivedState(currColumn?.name ?? '', [currColumn]);

    const handleSort = (direction: SheetSortDirection) => {
        addRule(columnKey, direction);
    };

    if (!currColumn) {
        return null;
    }

    const edit =
        currColumn.type !== CustomColumnType.Number ? (
            <ContextMenu.Item
                item={{
                    icon: Edit,
                    label: LL.EDIT(),
                    disabled: referencing.columns,
                    action: () => {
                        showModal(
                            <CustomColumnModal
                                columnIdx={customColumnIdx}
                                customColumns={customColumns}
                                updateCustomColumns={updateCustomColumns}
                                customColumnLabels={customColumnLabels}
                            />
                        );
                    },
                }}
            />
        ) : null;

    return (
        <ContextMenu {...menuProps} placement="bottom-start">
            {canEdit && (
                <>
                    <ContextMenu.Item
                        item={{
                            render(props) {
                                return (
                                    <FlexRow style={{ padding: 1, width: '100%' }}>
                                        <TextInput
                                            value={editableName}
                                            placeholder={LL.COLUMN_NAME()}
                                            onChange={setEditableName}
                                            onKeyDown={async (e) => {
                                                if (e.key !== 'Enter') return;

                                                await updateCustomColumns(
                                                    $updateItem<CustomColumnRepresentation>(
                                                        (c) => c.id === currColumn.id,
                                                        {
                                                            name: editableName.trim().length ? editableName : null,
                                                        }
                                                    )
                                                );
                                                props.closeMenu();

                                                // Avoid :focus-visible style
                                                setTimeout(() => (document.activeElement as HTMLElement)?.blur());
                                            }}
                                        />
                                    </FlexRow>
                                );
                            },
                        }}
                    />
                    <ContextMenu.Divider />
                    {edit}
                    <ContextMenu.Item
                        item={{
                            icon: Copy,
                            label: LL.DUPLICATE(),
                            disabled: referencing.columns,
                            action: async () => {
                                if (!currColumn) return;
                                const cols = [
                                    ...customColumns.slice(0, customColumnIdx + 1),
                                    { ...currColumn, id: generateUniqueId(customColumns) },
                                    ...customColumns.slice(customColumnIdx + 1),
                                ];
                                await updateCustomColumns(cols);
                                logEvent('sheet/#/columns/#/duplicate');
                            },
                        }}
                    />
                </>
            )}
            <ContextMenu.Item
                item={{
                    icon: Sort,
                    label: LL.SORT(),
                    disabled: referencing.sort,
                }}
            >
                <ContextMenu.Item
                    item={{
                        icon: ArrowUp,
                        label: LL.ASCENDING(),
                        disabled: referencing.sort,
                        action: () => {
                            handleSort(SheetSortDirection.Asc);
                        },
                    }}
                />
                <ContextMenu.Item
                    item={{
                        icon: ArrowDown,
                        label: LL.DESCENDING(),
                        disabled: referencing.sort,
                        action: () => {
                            handleSort(SheetSortDirection.Desc);
                        },
                    }}
                />
            </ContextMenu.Item>
            <ContextMenu.Divider />
            <SheetFreezeMenu columnIndex={columnIndex} />
            {canEdit && (
                <>
                    <ContextMenu.Divider />
                    <ContextMenu.Item
                        item={{
                            icon: () =>
                                currColumn.readonly ? (
                                    <Checked
                                        color={
                                            currColumn.type === CustomColumnType.Comparison
                                                ? appColors.neutral.w700
                                                : appColors.primary.w500
                                        }
                                    />
                                ) : (
                                    <UnChecked />
                                ),
                            label: LL.LOCK_COLUMN(),
                            disabled: referencing.columns,
                            action: async () => {
                                if (!currColumn) return;
                                await updateCustomColumns({ [customColumnIdx]: { readonly: $toggle() } });
                                logEvent('sheet/#/columns/#');
                            },
                        }}
                    />
                    {currColumn.type === CustomColumnType.Number ? (
                        <ContextMenu.Item
                            item={{
                                icon: () =>
                                    currColumn.bindToTimeline ? (
                                        <Checked color={appColors.primary.w500} />
                                    ) : (
                                        <UnChecked />
                                    ),
                                label: LL.BIND_TO_TIMELINE(),
                                disabled: referencing.columns,
                                action: async () => {
                                    if (!currColumn) return;
                                    await updateCustomColumns({
                                        [customColumnIdx]: { bindToTimeline: !currColumn.bindToTimeline },
                                    });
                                    logEvent('sheet/#/columns/#');
                                },
                            }}
                        />
                    ) : null}
                    <ContextMenu.Divider />
                    <ContextMenu.Item
                        item={{
                            icon: Delete,
                            label: LL.DELETE(),
                            disabled: referencing.columns,
                            action: () => {
                                showModal(
                                    <Confirmation
                                        $color="error"
                                        title={`${LL.DELETE()} ${currColumn.name || ''}`}
                                        confirmLabel={LL.DELETE()}
                                        question={LL.DELETE_CUSTOM_COLUMN_CONFIRMATION()}
                                        onConfirm={async () => {
                                            if (!currColumn) return;
                                            await updateCustomColumns($splice(customColumnIdx, 1));
                                            logEvent('sheet/#/columns/#/delete');
                                            hideModal();
                                        }}
                                        onCancel={hideModal}
                                    />
                                );
                            },
                        }}
                    />
                </>
            )}
        </ContextMenu>
    );
};
